// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import type {
  ICommunicationResponse,
  ICommunicationRequest,
  IAlarmAckRequest,
  IWriteItemRequest,
  LogHandle,
  ILogDataTrend,
  ILogDataIo,
  LogAggregation,
  LogAggregationType,
} from "shared/types/linxapi";
import type { IAlarmEntry } from "shared/types/globtypes";
import type { ControllerAlarmSource } from "shared/types/parameter";
import { Aggregation, AggregationType } from "graphql/api";

export interface ILogReadOptions {
  startDate?: number; // s
  endDate?: number; // s, exclusive
  aggregation?: Aggregation;
  aggregationType?: AggregationType; // only for IO logs
  withPreviousValues?: boolean; // only for raw IO logs
}

// Transport-agnostic client for the controller /DA protocol. It builds
// ICommunicationRequest payloads and parses ICommunicationResponse results;
// the concrete transport (HTTP POST for a direct LINX connection, WebSocket
// for the cloud proxy) is provided by subclasses via doRequest().
export abstract class ComInterface {
  public timeout: number | undefined;

  // perform a single /DA request and return the parsed response
  protected abstract doRequest(body: ICommunicationRequest): Promise<ICommunicationResponse>;

  public async read(opcTags: string | string[], clientHandles?: string | string[]) {
    // convert potential single tag to array
    if (typeof opcTags === "string") {
      opcTags = [opcTags];
    }
    // build item query
    const readItems = opcTags;
    const jsonBody: ICommunicationRequest = {
      Options: {
        ClientRequestHandle: clientHandles,
      },
      Read: readItems,
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    const readResult = response.ReadResponse;
    return readResult?.length === 1 ? readResult[0] : readResult;
  }

  public async write(
    opcTags: string | string[],
    _types: string | string[],
    values: string | number | (string | number)[],
  ): Promise<boolean> {
    // convert potential single tag to array
    if (!Array.isArray(opcTags)) {
      opcTags = [opcTags];
    }
    if (!Array.isArray(values)) {
      values = [values];
      console.assert(values.length === opcTags.length, "Illegal OPC write values");
    }
    // the types may be a single entry even for arrays if all items have same type

    const writeItems = [];
    for (let i = 0; i < opcTags.length; i++) {
      // item types unused
      writeItems.push({
        ItemName: opcTags[i],
        ClientItemHandle: opcTags[i],
        Value: values[i],
      });
    }
    const jsonBody: ICommunicationRequest = {
      Options: {
        ClientRequestHandle: "HttpClient",
      },
      Write: writeItems,
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    const writeResult = response.WriteResponse;
    return (
      Array.isArray(writeResult) &&
      writeResult.length > 0 &&
      writeResult.every((curItem: boolean) => curItem)
    );
  }

  public async writeItem(items: IWriteItemRequest | IWriteItemRequest[]): Promise<boolean> {
    // convert potential single item to array
    if (!Array.isArray(items)) {
      items = [items];
    }
    const jsonBody: ICommunicationRequest = {
      Options: {
        ClientRequestHandle: "HttpClient",
      },
      WriteItem: items,
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    const writeResult = response.WriteItemResponse;
    return (
      Array.isArray(writeResult) &&
      writeResult.length > 0 &&
      writeResult.every((curItem: boolean) => curItem)
    );
  }

  public async readAlarms() {
    const alarmJson = (await this.read("AlarmSummary")) as string | undefined;
    let alarmItems: IAlarmEntry<ControllerAlarmSource>[] = [];
    try {
      alarmItems = JSON.parse(alarmJson ?? "") as IAlarmEntry<ControllerAlarmSource>[];
    } catch (_e) {
      console.warn("cannot parse alarm summary", alarmJson);
    }
    return alarmItems;
  }

  public async ackAlarm(source: ControllerAlarmSource, value: number, alarmTimestamp: number) {
    const ackRequest: IAlarmAckRequest = {
      source,
      value,
      alarmTimestamp,
      ackSource: "embyt LINX web",
    };
    const response = await this.write("AlarmAck", "string", JSON.stringify(ackRequest));
    return response;
  }

  // get item counts and time boundaries of the given logs
  public async getLogInfo(logHandles: LogHandle[]) {
    const jsonBody: ICommunicationRequest = {
      LogInfo: logHandles.map((curHandle) => ({ LogHandle: curHandle })),
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    if (!response || !Array.isArray(response.LogInfoResponse)) {
      throw new Error("Got unexpected log info result");
    }
    return response.LogInfoResponse;
  }

  public async logRead(logHandle: LogHandle, numItems: number, options: ILogReadOptions) {
    const jsonBody: ICommunicationRequest = {
      LogRead: {
        LogHandle: logHandle,
        NumItems: numItems,
        StartDateTime: options.startDate,
        EndDateTime: options.endDate,
        Aggregation:
          options.aggregation === Aggregation.NONE ?
            undefined
          : (options.aggregation as LogAggregation | undefined),
        AggregationType: options.aggregationType as LogAggregationType | undefined,
        WithPreviousValues: options.withPreviousValues,
      },
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    const readResult = response.LogReadResponse as ILogDataTrend[] | ILogDataIo[];
    return readResult;
  }

  public async deviceInfo() {
    // build query
    const jsonBody: ICommunicationRequest = {
      GetDeviceInfo: true,
    };
    // perform request
    const response = await this.doRequest(jsonBody);
    return response.GetDeviceInfoResponse!;
  }
}
